import dayjs, { type Dayjs } from 'dayjs';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { useHerbicideActivity } from '../../use-campaign-registry-activity';
import type { StageMessage } from './shared';

/** Title/message vary by how close the planting date is; the two actions are always the same. */
const copyForPlanting = (plantingDate: Dayjs): { title: string; message: string } => {
    const now = dayjs();
    if (now.isBefore(plantingDate.subtract(1, 'month'), 'day')) {
        return {
            title: _('Revisá el manejo de malezas'),
            message: _(
                'Definí productos y dosis para el barbecho según el ambiente del lote y compartilo con el productor antes del inicio de campaña'
            ),
        };
    }
    if (now.isBefore(plantingDate.subtract(1, 'week'), 'day')) {
        return {
            title: _('Se acerca la siembra'),
            message: _(
                'Recomendá el herbicida y compartilo con el productor para arrancar la campaña con el lote libre de malezas.'
            ),
        };
    }
    return {
        title: _('Semana de siembra'),
        message: _(
            'El productor está sembrando. Recomendá el herbicida y compartilo para comenzar la campaña con el lote libre de malezas.'
        ),
    };
};

/**
 * "Siembra" (planting) banner.
 *
 * Shown while no weed management has been done (no herbicide application registered manually and
 * no herbicide/weed recommendation) over the window that runs from before planting through V1:
 * - all crops: while the planting date hasn't arrived yet, with copy that sharpens as it
 *   approaches (>1 month / >1 week / same week);
 * - corn: also after planting, up to and including V1 (before V2 and before the crop leaves the
 *   vegetative phase), so the weed-management nudge stays through early emergence.
 *
 * FieldView applications are intentionally not considered here. Returns `null` outside the
 * window or once any herbicide activity exists.
 */
export const useSiembraStageMessage = ({
    cropMonitoring,
    field,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const corn = cropMonitoring?.cornCropMonitoring;
    const plantingDate = cropMonitoring?.plantingDate ? dayjs(cropMonitoring.plantingDate) : null;
    const isPrePlanting = plantingDate !== null && dayjs().isBefore(plantingDate, 'day');
    const isCornThroughV1 =
        cropMonitoring?.crop === 'corn' &&
        (corn?.phenology == null || corn.phenology === 'planting' || corn.phenology === 'emergence') &&
        (corn?.vegetativeStage == null || corn.vegetativeStage <= 1);
    const isSiembra = Boolean(cropMonitoring?.active) && (isPrePlanting || isCornThroughV1);

    const { hasHerbicideApplication, hasHerbicideRecommendation, isLoading } = useHerbicideActivity({
        fieldId: isSiembra ? field.id : undefined,
        cropMonitoring,
        enabled: isSiembra,
    });

    if (!isSiembra || plantingDate === null) return null;
    if (isLoading) return null;
    if (hasHerbicideApplication || hasHerbicideRecommendation) return null;

    return {
        ...copyForPlanting(plantingDate),
        actions: [
            { label: _('Registrar aplicación'), variant: 'contained', action: 'register-application' },
            { label: _('Recomendar herbicidas'), variant: 'outlined', action: 'recommend-herbicide' },
        ],
    };
};
